import '../../styles/styles.css';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import Layout from '../../components/Layout';

import AnimateIn from '../../components/AnimateIn';
import VimeoVideo from '../../components/VimeoVideo';
import Image from '../../components/Image';
import ColumnLists from '../../components/ColumnLists';
import LinkButton from '../../components/LinkButton';
import LinkList from '../../components/LinkList';
import Quote from '../../components/Quote';
import WorkHeader from '../../components/WorkHeader';

import { getProject } from '../../config/general';
import hardcodedStaticProps from '../../lib/harcodedStaticProps';

const Samsung = ({featuredProjectsList}) => {
  const project = getProject('samsung');

  return (
    <Layout metaData={{ data: project }} featuredProjectsList={featuredProjectsList}>
      <article className="components page">
        <WorkHeader component={{ project }} />

        <VimeoVideo
          className="g-row"
          component={{
            posterVideo: '215979496'
          }}
        />

        <AnimateIn className="text g-row">
          <p>
            When SmartThings went from a successful Kickstarter campaign to a Samsung subsidiary, they had to scale up fast.
          </p>
          <p>
            To support the rapid growth, Your Majesty was hired in to work as an extension of the global brand and marketing teams.
          </p>
        </AnimateIn>

        <Image
          className="g-row"
          component={{
            image:
              '//images.yourmajesty.co/20170511070428/04-st-logo-img-e1494486373378.png',
            calculateRatio: '1920,720'
          }}
        />

        <AnimateIn className="text g-row">
          <h2> A brand and marketing refresh</h2>
          <p>
            The SmartThings brand ecosystem had grown organically since the company’s founding in 2012. This resulted in inconsistent and scattered brand messaging that confused customers and employees, plus hindered global scale.
          </p>
          <p>
            As one of the first assignments, we got the opportunity to bring consistency to the brand.
          </p>
    
        <Image
          className="component--no-margin"
          component={{
            image: '//images.yourmajesty.co/20170511071924/02-st-hero-img.png'
          }}
        />

        <VimeoVideo
          className="component--no-margin"
          component={{
            posterVideo: '215204437'
          }}
        />

        <Image
          component={{
            image: '//images.yourmajesty.co/20170509172444/smartthings_packaging_case_hero_final1.jpg'
          }}
        />
        </AnimateIn>
        <AnimateIn className="text g-row">
          <h2>A full service relationship</h2>
          <p>
            Following the brand identity redesign, we worked closely with SmartThings over five years, to roll out a series of brand and marketing programs, including:
          </p>
          <ul>
            <li>Packaging redesign</li>
            <li>Omnichannel content</li>
            <li>Website and ecommerce</li>
            <li>Retail interactive experiences</li>
            <li>Retail merchandising</li>
            <li>Online merchandising</li>
            <li>Digital Banners</li>
            <li>Marketing collateral</li>
            <li>Channel and sales toolkits</li>
            <li>Retail sales training materials</li>
          </ul>
        </AnimateIn>
        <AnimateIn className="text g-row">
          <h2 id="a-flexible-communication-system">
            A flexible communication system
          </h2>
          <p>
            SmartThings is an open platform and works with a wide range of
            devices from varying brands.
          </p>
          <p>
            With an ever expanding partner catalogue we needed a communication
            system that was fast and flexible to work with. We also wanted to
            further enforce SmartThings new, friendlier, tone of voice.
          </p>
          <p>
            The solve was a world of simplistic and quirky illustrations with
            characters that could be used and re-used across all communications.
          </p>
        </AnimateIn>

        <VimeoVideo
          className="g-row"
          component={{
            video: '215963904',
            posterVideo: '215963904'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="education-and-inspiration-in-retail-and-online">
            Education and inspiration in retail and online
          </h2>
          <p>
            With IoT still being considered in its infancy, we needed to speak
            to the average homeowners and educate them that having a smart home
            isn’t just about flipping on the lights with your smartphone.
          </p>
          <p>
            For retail, our team iterated through prototyping and user tests to
            design an interactive retail fixture that draws attention and
            educates them how to get started.
          </p>
        </AnimateIn>

        <Image
          className="g-row component--no-margin"
          component={{
            image: '//images.yourmajesty.co/20171122112547/170425-pandl-fixture3.jpg'
          }}
        />

        <Image
          className="g-row component--no-margin"
          component={{
            image: '//images.yourmajesty.co/20170509172510/play-and-learn-11.jpg'
          }}
        />

        <VimeoVideo
          className="g-row"
          component={{
            video: '235526788',
            posterVideo:'235526788',
            caption:
              'The fixtures rolled out in 2,000 stores in the US and the UK, including BestBuy, Home Depot, John Lewis and Dixon’s.'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="scalable-website-build">Scalable website build</h2>
          <p>
            For the redesign of{' '}
            <a href="http://smartthings.com" target="_blank" rel="noopener">
              smartthings.com,
            </a>
            {' '}
            we began with examining data and performed user testing to inform the direction we wanted to take. 
            The result was an uncluttered online platform that guides, educates, and delights its visitors about the value proposition and SmartThings ecosystem.
          </p>
        </AnimateIn>

        <Image
          className="component--no-margin"
          component={{
            image: '//images.yourmajesty.co/20170509172523/smartthings-digital-web-static-111.jpg'
          }}
        />

        <Image
          className="component--no-margin"
          component={{
            image: '//images.yourmajesty.co/20170511071520/14-st-web-screen-1.png',
            calculateRatio: '1929,1185'
          }}
        />

        <Image
          component={{
            image: '//images.yourmajesty.co/20170511071624/16-st-web-styleguide.jpg',
            calculateRatio: '1929,1185'
          }}
        />

        <AnimateIn className="text g-row">
          <h2>Built from the ground up:</h2>
          <ul>
            <li>Powered by Contentful CMS.</li>
            <li>Component-based design system.</li>
            <li>
              Scalable and adaptable to ever changing business needs.
            </li>
          </ul>
        </AnimateIn>

        <AnimateIn className="text g-row">
          <h2 id="iteration-towards-perfection">Business outcomes</h2>
          <p>
            12 months after the new brand and marketing roll-out, US sales saw an increase of 150%.
          </p>        
        </AnimateIn>

        <AnimateIn className="text g-row">
          <h2 id="curious-to-know-more-">Curious to know more?</h2>
          <p>
            Learn more about this and similar engagements by sending us a note to{' '}
            <a href="mailto: hello@yourmajesty.co">
              hello@yourmajesty.co
            </a>
            , or check out our case-studies for{' '}
            <Link href="/work/samsung-cl">
              <a>Samsung</a>
            </Link>{' '}
            or{' '}
            <Link href="/work/dopper">
              <a>Dopper</a>
            </Link>
            .
          </p>

          <h2 id="collaborators">Collaborators</h2>
          <p>
            D!srupt
            <br />
            Cléa Dieudonné
            <br />
            <a href="http://cargocollective.com/40redapples" target="_blank" rel="noopener">
              David Schagerström
            </a>
          </p>
        </AnimateIn>

        <ColumnLists
          className="g-row"
          component={{
            title: 'Services',
            columns: [
              {
                title: 'Strategy',
                items: [
                  'Brand Strategy',
                  'Collaborative Working Sessions',
                  'Communications Strategy',
                  'Content Strategy',
                  'Creative Strategy',
                  'Experience Mapping',
                  'Interviews & User Testing',
                  'Launch Strategy',
                  'Product Strategy',
                  'Prototyping & Validation',
                  'Research',
                  'Roadmapping',
                  'Technical Strategy',
                  'UX Strategy',
                  'Workshop & Facilitation'
                ]
              },

              {
                title: 'Design',
                items: [
                  '3D Animation',
                  '3D Design',
                  'Art Direction',
                  'Brand Design',
                  'Creative Direction',
                  'Illustration',
                  'Motion Design',
                  'Sound Design',
                  'UI/UX Design',
                  'Visual Design'
                ]
              },

              {
                title: 'Technology',
                items: [
                  'Back-end Development',
                  'Content Management System',
                  'eCommerce',
                  'Front-end Development',
                  'Functional Prototyping',
                  'Localization',
                  'Systems Architecture'
                ]
              },

              {
                title: 'Delivery',
                items: [
                  'A/B Testing',
                  'Analytics & Insights',
                  'Integration Support',
                  'Launch',
                  'Monitoring',
                  'Optimization',
                  'Quality Assurance',
                  'Staff Training & Support'
                ]
              },

              {
                title: 'Content',
                items: ['Animation', 'Photography', 'Post Production']
              },

              {
                title: 'Experiential',
                items: [
                  'Hardware Specification',
                  'Immersive Environment',
                  'Installation Prototyping',
                  'Interactive Environment'
                ]
              }
            ]
          }}
        />
      </article>
    </Layout>
  );
};

export { hardcodedStaticProps as getStaticProps };

export default Samsung;
